import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    AppBar,
    AppBarProps,
    IconButton,
    TextField,
    Toolbar,
    Typography,
    Box, Button, InputAdornment, Container, useMediaQuery,
} from "@mui/material";
import {ArrowBack, Clear, Search} from "@mui/icons-material";
import {makeStyles} from "@mui/styles";
import {PortTheme} from "~/ui/theme";
import {useBooleanHistoryState} from "~/apps/core/utils";
import {useHistory} from "react-router-dom";
import {ArrowBackDesktop} from "~/ui/icons";


export type SearchAppBarProps = Omit<AppBarProps, "onChange"> & {
    query?: string
    placeholder?: string
    children?: React.ReactNode
    title?: React.ReactNode
    navigationIcon?: React.ReactNode
    actions?: React.ReactNode
    disabled?: boolean
    onSearch?: (query: string) => any
}


const useStyles = makeStyles<PortTheme>(theme => ({
    input: {
        [`
        & input[type="search"]::-webkit-search-decoration,
        & input[type="search"]::-webkit-search-cancel-button,
        & input[type="search"]::-webkit-search-results-button,
        & input[type="search"]::-webkit-search-results-decoration
        `]: {
            "-webkit-appearance": "none",
        },
        "& input": {
            lineHeight: "inherit"
        },
        "& .MuiInputBase-root": {
            borderStyle: "solid",
            borderWidth: 1,
            borderColor: "#6E798C12",
            boxSizing: "border-box",
        },
    },
    searchHistory: {
        position: "absolute",
        width: "100%",
        height: "100vh",
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary
    },
    toolbar: {
        width: "100%",
        display: "block"
    },
    submitButton: {
        borderRadius:"4px",
    }
}));


export const SearchAppBar = (props: SearchAppBarProps) => {
    const classes = useStyles(props);
    const {
        children, title, navigationIcon, actions, placeholder, disabled, query: defaultQuery, onSearch, ...other
    } = props;
    const history = useHistory();
    const [editing, setEditing] = useBooleanHistoryState("app-bar-search");
    const [query, setQuery] = useState(defaultQuery);
    const $input = useRef<HTMLInputElement>();

    const matches = useMediaQuery('(min-width:1024px)');

    // update value from prop.query
    useEffect(() => {
        setQuery(defaultQuery);
    }, [defaultQuery]);

    const enter = () => {
        onSearch && onSearch(query);
        if (!query) {
            setEditing(false);
        }
        $input?.current?.blur();
    }

    const handleStartEditClick = () => {
        setEditing(true).then(() => {
            setTimeout(() => $input?.current?.focus());
        });
    }

    const handleBackClick = () => {
        history.goBack();
    }

    const handleClearClick = () => {
        setQuery("");
        $input?.current?.focus();
    }

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
    }

    const handleSearchClick = () => {
        enter();
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key == "Enter") {
            enter();
        }
    }

    const handleBack = () => {
        history.goBack();
    }

    return (
        <AppBar {...other}>
            <Container disableGutters>
                <Toolbar>
                    {editing ? (
                        <>
                            <IconButton onClick={handleBackClick} color="primary" edge="start">
                                <ArrowBack/>
                            </IconButton>
                            <TextField
                                placeholder={placeholder}
                                variant="filled"
                                size="small"
                                fullWidth
                                value={query}
                                onChange={handleQueryChange}
                                inputRef={$input}
                                disabled={disabled}
                                type="search"
                                className={classes.input}
                                onKeyPress={handleKeyPress}
                                InputProps={{
                                    disableUnderline: true,
                                    margin: "none",
                                    endAdornment: query && (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClearClick} disabled={disabled} size="small" color="primary">
                                                <Clear color="primary" fontSize="small"/>
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Box mr={-1} ml={1}>
                                <Button
                                    color="additionalPrimary"
                                    size="small"
                                    onClick={handleSearchClick}
                                    disabled={disabled}
                                    className={classes.submitButton}
                                >
                                    Найти
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <IconButton onClick={handleBack} color="primary" edge="start">
                                {navigationIcon}
                            </IconButton>
                            <Typography variant="menuTitle" style={{ flexGrow: 1 }} color="primary">
                                {title}
                            </Typography>
                            <IconButton onClick={handleStartEditClick} disabled={disabled} color="primary" edge="end">
                                <Search/>
                            </IconButton>
                            {actions}
                        </>
                    )}
                </Toolbar>
                <div className={classes.toolbar}>
                    {children}
                </div>
            </Container>
        </AppBar>
    );
}

SearchAppBar.defaultProps = {
    placeholder: "Поиск",
    suggestions: [],
    navigationIcon: <ArrowBack/>
}
